'use client'

import { Logo } from '@/components/brand/Logo'
import { ThemeSwitch } from '@/components/shell/ThemeSwitch'
import { useRouter } from 'next/navigation'
import { useEffect, useId, useRef, useState } from 'react'
import { Button, Icon, IconButton, LangSwitch } from '../../design-system/index.js'
import { NAV_HREFS } from '../../content/landing-nav'
import { wrap } from '../layout.js'
import './SiteHeader.css'

/**
 * Sticky header: wordmark, nav (desktop), language switch and the primary CTA,
 * set in a floating pill over the hero's backdrop.
 *
 * On mobile the CTA is replaced by a menu button, which opens a disclosure
 * holding the same nav links — the design shows the button but has nowhere for
 * it to lead, and a control that does nothing is unusable by keyboard and
 * screen-reader users.
 *
 * The only client component on the landing page besides the FAQ accordion
 * (T-PERF-01): which of the two layouts shows is decided by CSS
 * (`.u-desktop-only` / `.u-mobile-only` in landing.css), so this holds just
 * the menu's open state and the language switch. `t` is the slice of the
 * copy the header shows, not the whole page's.
 *
 * Language switching navigates to the sibling locale route and sets the
 * `NEXT_LOCALE` cookie next-intl's middleware reads on the next request
 * (T-WEB-03); the server then renders the page in that language.
 */
const LOCALE_COOKIE = 'NEXT_LOCALE'

export function SiteHeader({ t, lang, authHref, signedIn = false }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const menuId = useId()
  const toggleRef = useRef(null)
  const router = useRouter()

  // Keep the document language in step so screen readers pick the right
  // voice. The root layout sets `<html lang>` on a full load; this is the
  // guard for the client-side transition a language switch makes.
  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  const onLangChange = (next) => {
    document.cookie = `${LOCALE_COOKIE}=${next}; path=/; max-age=31536000`
    router.replace('/' + next)
  }

  useEffect(() => {
    if (!menuOpen) return
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false)
        toggleRef.current?.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [menuOpen])

  return (
    <header className="u-header">
      <div style={{ ...wrap(), paddingTop: 12, paddingBottom: 12 }}>
        <div
          className="u-header-bar"
          style={{
            height: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            padding: 'var(--l-header-pad)',
          }}
        >
          <a href="#top" className="u-wordmark" style={{ fontSize: 22 }}>
            <Logo size={30} />
          </a>

          <nav
            aria-label={t.mainNavLabel}
            className="u-desktop-only"
            style={{ display: 'flex', gap: 28, fontSize: 15 }}
          >
            {t.nav.map((n, i) => (
              <a
                key={n}
                href={NAV_HREFS[i]}
                className="u-nav-link"
                style={{ color: 'var(--ink-2)' }}
              >
                {n}
              </a>
            ))}
          </nav>

          <div className="u-header-actions" style={{ display: 'flex', alignItems: 'center' }}>
            <LangSwitch value={lang} onChange={onLangChange} label={t.langLabel} />
            <ThemeSwitch toLight={t.themeToLight} toDark={t.themeToDark} className="u-theme-switch" />
            <span className="u-desktop-only" style={{ display: 'contents' }}>
              <Button size="sm" href={authHref}>
                {signedIn ? t.openCabinet : t.cta}
              </Button>
            </span>
            <span className="u-mobile-only" style={{ display: 'contents' }}>
              <IconButton
                ref={toggleRef}
                plain
                label={t.menu}
                aria-expanded={menuOpen}
                aria-controls={menuId}
                onClick={() => setMenuOpen((v) => !v)}
              >
                <Icon name={menuOpen ? 'x' : 'menu'} />
              </IconButton>
            </span>
          </div>
        </div>

        <div id={menuId} className="u-header-menu u-mobile-only" hidden={!menuOpen}>
          <nav aria-label={t.mainNavLabel} style={{ padding: '12px 18px 16px' }}>
            <ul className="u-header-menu-list">
              {t.nav.map((n, i) => (
                <li key={n}>
                  <a href={NAV_HREFS[i]} onClick={() => setMenuOpen(false)}>
                    {n}
                  </a>
                </li>
              ))}
              <li>
                <Button full href={authHref}>
                  {signedIn ? t.openCabinet : t.cta}
                </Button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </header>
  )
}

export default SiteHeader
